import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import type {
  McpConnection,
  McpAgentToken,
  CreateAgentTokenRequest,
  CreateAgentTokenResponse,
  McpAuditPage,
} from '@clareefai/types';

// Management of MCP agent access (docs/MCP_SERVER_PLAN.md §6). Firebase-authed; baseUrl
// already ends in /api, so paths are relative to that.
export const mcpApi = createApi({
  reducerPath: 'mcpApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['McpConnection', 'McpAgentToken', 'McpAuditLog'],
  endpoints: (builder) => ({
    getMcpConnections: builder.query<McpConnection[], void>({
      query: () => '/vendors/me/mcp/connections',
      providesTags: ['McpConnection'],
    }),
    revokeMcpConnection: builder.mutation<void, string>({
      query: (id) => ({ url: `/vendors/me/mcp/connections/${id}`, method: 'DELETE' }),
      invalidatesTags: ['McpConnection'],
    }),
    getMcpAgentTokens: builder.query<McpAgentToken[], void>({
      query: () => '/vendors/me/mcp/agent-tokens',
      providesTags: ['McpAgentToken'],
    }),
    createMcpAgentToken: builder.mutation<CreateAgentTokenResponse, CreateAgentTokenRequest>({
      query: (body) => ({ url: '/vendors/me/mcp/agent-tokens', method: 'POST', body }),
      invalidatesTags: ['McpAgentToken'],
    }),
    revokeMcpAgentToken: builder.mutation<void, string>({
      query: (id) => ({ url: `/vendors/me/mcp/agent-tokens/${id}`, method: 'DELETE' }),
      invalidatesTags: ['McpAgentToken'],
    }),
    getMcpAuditLogs: builder.query<McpAuditPage, { page?: number; size?: number; caller?: string }>({
      query: ({ page = 0, size = 50, caller }) => ({
        url: '/vendors/me/mcp/audit-logs',
        params: { page, size, ...(caller ? { caller } : {}) },
      }),
      providesTags: ['McpAuditLog'],
    }),
  }),
});

export const {
  useGetMcpConnectionsQuery,
  useRevokeMcpConnectionMutation,
  useGetMcpAgentTokensQuery,
  useCreateMcpAgentTokenMutation,
  useRevokeMcpAgentTokenMutation,
  useGetMcpAuditLogsQuery,
} = mcpApi;
